import type { ClientOrganization } from '@DigitaleDoerfer/digitale-doerfer-api';

import resolveOrganizationAccess, {
  type OrganizationAccessRequirement,
  type OrganizationAccessState
} from '@/shared/components/organization/organizationAccess';
import { useOrganizationAccessValue } from '@/shared/components/organization/useOrganizationAccessValue';

/**
 * Standalone counterpart to {@link useOrganizationAccess}: evaluates the access
 * rules for the given organization without requiring an
 * {@link OrganizationAccessProvider} ancestor. Mirrors the standalone mode of
 * {@link OrganizationAccessGuard} — use this when a component needs a computed
 * boolean (e.g. to gate a menu item) rather than to wrap children, or when many
 * *different* organizations are evaluated (e.g. per item in a list) where a
 * single provider cannot apply.
 *
 * Fails closed while the organization is still loading (`undefined`), matching
 * the resolver's `Denied` default.
 */
export const useOrganizationAccessFor = (
  organization: ClientOrganization | undefined,
  requirement?: OrganizationAccessRequirement
): OrganizationAccessState => {
  const contextValue = useOrganizationAccessValue(organization);
  return resolveOrganizationAccess(contextValue, requirement);
};
